import React, { useMemo, useRef, useEffect } from "react";
import {
  addDays,
  format,
  isSameDay,
  isToday,
  startOfDay,
  differenceInMinutes,
} from "date-fns";
import { nl } from "date-fns/locale";
import {
  SharedCalendarEvent,
  OWNER_STYLES,
  inferEventType,
} from "./types";

interface WeekGridProps {
  weekStart: Date; // Monday
  events: SharedCalendarEvent[];
  onEventClick: (event: SharedCalendarEvent) => void;
  /** Hour at the very top of the grid (default 6 AM). The grid always
   *  shows all 24 hours, with hours before startHour wrapping to the bottom. */
  startHour?: number;
}

const HOUR_HEIGHT_PX = 56;
const GUTTER_WIDTH_PX = 64;
const HOURS_IN_DAY = 24;

interface PositionedEvent {
  event: SharedCalendarEvent;
  topPx: number;
  heightPx: number;
  /** 0-based index within overlap group */
  laneIndex: number;
  laneCount: number;
}

const minutesFromDayStart = (date: Date): number => {
  const dayStart = startOfDay(date);
  return differenceInMinutes(date, dayStart);
};

/**
 * Map a "minutes since 00:00 today" value into a pixel offset, taking the
 * grid's startHour into account. Hours before startHour wrap to the bottom
 * (e.g. with startHour=6, an event at 02:00 ends up after 23:00).
 */
const timeToOffsetPx = (minutesFromMidnight: number, startHour: number): number => {
  const startMin = startHour * 60;
  const diff = (minutesFromMidnight - startMin + HOURS_IN_DAY * 60) % (HOURS_IN_DAY * 60);
  return (diff / 60) * HOUR_HEIGHT_PX;
};

/**
 * Lay out overlapping events in lanes so they share the day column width.
 * Greedy column assignment based on the canonical "Outlook-like" approach.
 */
const layoutDayEvents = (
  events: SharedCalendarEvent[],
  startHour: number,
): PositionedEvent[] => {
  if (!events.length) return [];

  const sorted = [...events].sort(
    (a, b) =>
      new Date(a.start).getTime() - new Date(b.start).getTime() ||
      new Date(a.end).getTime() - new Date(b.end).getTime(),
  );

  const result: PositionedEvent[] = [];
  let cluster: SharedCalendarEvent[] = [];
  let clusterEnd = 0;

  const flushCluster = () => {
    if (!cluster.length) return;
    const lanes: Date[] = []; // lanes[i] = end time of last event in lane i
    const laneIndices: number[] = [];
    cluster.forEach((evt) => {
      const start = new Date(evt.start);
      const end = new Date(evt.end);
      let placed = false;
      for (let i = 0; i < lanes.length; i++) {
        if (lanes[i] <= start) {
          lanes[i] = end;
          laneIndices.push(i);
          placed = true;
          break;
        }
      }
      if (!placed) {
        laneIndices.push(lanes.length);
        lanes.push(end);
      }
    });
    const laneCount = lanes.length;
    cluster.forEach((evt, i) => {
      const start = new Date(evt.start);
      const end = new Date(evt.end);
      const startMin = minutesFromDayStart(start);
      const endMin = Math.min(minutesFromDayStart(end), HOURS_IN_DAY * 60);
      if (endMin <= startMin) return;
      const topPx = timeToOffsetPx(startMin, startHour);
      const heightPx = Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT_PX, 20);
      result.push({
        event: evt,
        topPx,
        heightPx,
        laneIndex: laneIndices[i],
        laneCount,
      });
    });
    cluster = [];
  };

  sorted.forEach((evt) => {
    const start = new Date(evt.start).getTime();
    const end = new Date(evt.end).getTime();
    if (cluster.length === 0 || start < clusterEnd) {
      cluster.push(evt);
      clusterEnd = Math.max(clusterEnd, end);
    } else {
      flushCluster();
      cluster = [evt];
      clusterEnd = end;
    }
  });
  flushCluster();

  return result;
};

export const WeekGrid: React.FC<WeekGridProps> = ({
  weekStart,
  events,
  onEventClick,
  startHour = 6,
}) => {
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  // Rotated list — e.g. [6,7,8,...,23,0,1,2,3,4,5] for startHour=6
  const hours = useMemo(
    () =>
      Array.from(
        { length: HOURS_IN_DAY },
        (_, i) => (startHour + i) % HOURS_IN_DAY,
      ),
    [startHour],
  );

  // Pre-bucket events per day (timed + all-day) for fast lookup
  const eventsByDayIdx = useMemo(() => {
    const buckets: SharedCalendarEvent[][] = days.map(() => []);
    const allDay: SharedCalendarEvent[][] = days.map(() => []);
    events.forEach((evt) => {
      if (!evt.start || !evt.end) return;
      const start = new Date(evt.start);
      days.forEach((day, idx) => {
        if (isSameDay(start, day)) {
          if (evt.isAllDay) allDay[idx].push(evt);
          else buckets[idx].push(evt);
        }
      });
    });
    return { timed: buckets, allDay };
  }, [days, events]);

  const totalGridHeightPx = HOURS_IN_DAY * HOUR_HEIGHT_PX;

  // Scroll container — initial scroll lands at 0 (the top, which is startHour)
  // and the user can drag down to see the wrap-around hours at the bottom.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [weekStart, startHour]);

  return (
    <div className="border-border overflow-hidden rounded-lg border bg-white">
      {/* Header row: day labels — sticky top of the calendar card */}
      <div
        className="border-border grid border-b bg-white"
        style={{
          gridTemplateColumns: `${GUTTER_WIDTH_PX}px repeat(7, 1fr)`,
        }}
      >
        <div className="border-border border-r" />
        {days.map((day) => {
          const today = isToday(day);
          return (
            <div
              key={day.toISOString()}
              className={`border-border border-r p-2 text-center last:border-r-0 ${
                today ? "bg-moss-primary/5" : ""
              }`}
            >
              <div className="text-neutral-secondary-mid text-xs font-medium uppercase">
                {format(day, "EEE", { locale: nl })}
              </div>
              <div
                className={`mt-1 text-lg font-semibold ${
                  today ? "text-moss-primary" : "text-neutral-primary"
                }`}
              >
                {format(day, "d")}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day row */}
      {eventsByDayIdx.allDay.some((d) => d.length > 0) && (
        <div
          className="border-border grid border-b"
          style={{
            gridTemplateColumns: `${GUTTER_WIDTH_PX}px repeat(7, 1fr)`,
          }}
        >
          <div className="text-neutral-secondary-mid border-border flex items-center justify-end border-r px-2 py-1 text-[11px] uppercase">
            Hele dag
          </div>
          {eventsByDayIdx.allDay.map((dayEvents, idx) => (
            <div
              key={idx}
              className="border-border space-y-1 border-r px-1 py-1 last:border-r-0"
            >
              {dayEvents.map((evt) => {
                const style = OWNER_STYLES[evt.owner];
                return (
                  <button
                    key={evt.id}
                    onClick={() => onEventClick(evt)}
                    className={`block w-full truncate rounded border px-2 py-0.5 text-left text-xs font-medium ${style.blockClass}`}
                    style={{
                      borderLeftWidth: 4,
                      borderLeftColor: style.accentHex,
                    }}
                  >
                    {evt.subject}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Scrollable timed area */}
      <div
        ref={scrollRef}
        className="overflow-y-auto"
        // Keep the calendar within the viewport — leave room for app header,
        // page header (~190px). Falls back to a sensible min on small screens.
        style={{ maxHeight: "calc(100vh - 220px)", minHeight: "60vh" }}
      >
        <div className="relative">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `${GUTTER_WIDTH_PX}px repeat(7, 1fr)`,
            }}
          >
            {/* Time gutter */}
            <div className="border-border border-r">
              {hours.map((h, i) => (
                <div
                  key={`${h}-${i}`}
                  className="text-neutral-secondary-mid relative pr-2 text-right text-[11px]"
                  style={{ height: `${HOUR_HEIGHT_PX}px` }}
                >
                  <span className="absolute -top-2 right-2 tabular-nums">
                    {String(h).padStart(2, "0")}:00
                  </span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            {days.map((day, idx) => {
              const dayEvents = eventsByDayIdx.timed[idx];
              const positioned = layoutDayEvents(dayEvents, startHour);
              const today = isToday(day);
              return (
                <div
                  key={day.toISOString()}
                  className={`border-border relative border-r last:border-r-0 ${
                    today ? "bg-moss-primary/5" : ""
                  }`}
                  style={{ height: `${totalGridHeightPx}px` }}
                >
                  {/* Hour grid lines */}
                  {hours.map((h, i) => (
                    <div
                      key={`${h}-${i}`}
                      className="border-border/60 border-b"
                      style={{ height: `${HOUR_HEIGHT_PX}px` }}
                    />
                  ))}

                  {/* Events */}
                  {positioned.map(({ event, topPx, heightPx, laneIndex, laneCount }) => {
                    const widthPct = 100 / laneCount;
                    const leftPct = laneIndex * widthPct;
                    const style = OWNER_STYLES[event.owner];
                    const type = inferEventType(event);
                    return (
                      <button
                        key={event.id}
                        onClick={() => onEventClick(event)}
                        className={`absolute overflow-hidden rounded border px-1.5 py-1 text-left text-[11px] shadow-sm transition-colors ${style.blockClass}`}
                        style={{
                          top: `${topPx}px`,
                          height: `${heightPx}px`,
                          left: `calc(${leftPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                          borderLeftWidth: 4,
                          borderLeftColor: style.accentHex,
                        }}
                        title={`${event.subject} — ${type}`}
                      >
                        <div
                          className="mb-0.5 inline-block rounded-sm px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-white"
                          style={{ backgroundColor: style.accentHex }}
                        >
                          {type}
                        </div>
                        <div className="line-clamp-2 font-semibold leading-tight">
                          {event.subject}
                        </div>
                        {heightPx > 44 && (
                          <div className="mt-0.5 text-[10px] opacity-80">
                            {format(new Date(event.start), "HH:mm")} —{" "}
                            {format(new Date(event.end), "HH:mm")}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
